// @ts-strict-ignore
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Card, DataListItem, Illustration, Input } from '@node-space/ui-components'
import { useRefundFeeEstimateMutation } from 'hooks/mutations/useRefundFeeEstimateMutation'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import {
  BaseErrorResponse,
  BeneficiaryTransferDestination,
  PaymentMethods,
} from 'types/beneficiaries'
import { RefundFeeResponse } from 'types/refund'
import { Transaction } from 'types/transaction'
import { WalletType } from 'types/types'
import { logSentryError } from 'utils/sentry'
import { generateReference, roundNumberWithCommas, stringToDecimal } from 'utils/utils'
import { RefundFormPayload, RefundFormPayloadKeys } from '../schemas/refundSchema'
import { RefundStep, RefundSteps } from '../types'
import { getMaximumRefundAmount } from '../utils'

interface RefundFormProps {
  form: UseFormReturn<RefundFormPayload>
  wallet: WalletType
  transaction: Transaction
  formValues: RefundFormPayload
  isPartiallyRefundable: boolean
  isRefundSheetOpen: boolean
  isInsufficientBalance: boolean
  setStep: (step: RefundStep) => void
  setIsInsufficientBalance: (isInsufficientBalance: boolean) => void
  handleOpenConvertSheet: () => void
  handleClose: () => void
  setWalletBalanceIsInsufficient: (isInsufficientBalance: boolean) => void
  walletDescription?: string
}

export const RefundForm = ({
  form,
  wallet,
  transaction,
  formValues,
  isPartiallyRefundable,
  isRefundSheetOpen,
  isInsufficientBalance,
  setStep,
  setIsInsufficientBalance,
  handleOpenConvertSheet,
  handleClose,
  setWalletBalanceIsInsufficient,
  walletDescription,
}: RefundFormProps) => {
  const { t } = useTranslation()
  const { enableUsLaunchRestrictions } = useFeatureFlags()
  const [refundFeeEstimateError, setRefundFeeEstimateError] = useState<BaseErrorResponse>()
  const [refundFeeEstimate, setRefundFeeEstimate] = useState<RefundFeeResponse>()

  const isInternational =
    transaction?.context?.depositDetails?.paymentMethod === PaymentMethods.SWIFT

  const maximumRefundableAmount = getMaximumRefundAmount(transaction)

  const {
    mutate: createRefundFeeEstimateRequest,
    isError: isCreateRefundFeeEstimateRequestError,
    isPending: isCreateRefundFeeEstimateRequestRequestLoading,
  } = useRefundFeeEstimateMutation()

  const handleRefundFeeEstimate = () => {
    const estimateRequest = {
      data: {
        value: parseFloat(formValues?.amount) || Number(maximumRefundableAmount),
        currency: transaction?.wallet?.currency?.code,
      },
      transactionReference: transaction?.context?.reference,
    }
    if (!transaction) {
      return
    }
    setRefundFeeEstimateError(null)
    createRefundFeeEstimateRequest(estimateRequest, {
      onSuccess: response => {
        setRefundFeeEstimate(response)
      },
      onError: (error: BaseErrorResponse) => {
        setRefundFeeEstimateError(error)
        logSentryError('Error: createRefundFeeEstimateRequest', error, {
          estimateRequest,
        })
      },
    })
  }

  useEffect(() => {
    if (!formValues?.reference) {
      form?.setValue('reference', generateReference())
    }
  }, [])

  useEffect(() => {
    if (transaction && isRefundSheetOpen) {
      handleRefundFeeEstimate()
    }
  }, [transaction, isRefundSheetOpen])

  const maxAvailableToRefund =
    refundFeeEstimate?.maxAvailableToRefund?.value || maximumRefundableAmount

  useEffect(() => {
    const hasValues = formValues?.amount && formValues?.reference && formValues?.transferDestination

    if (isRefundSheetOpen && !hasValues) {
      const transferDestination = isInternational
        ? BeneficiaryTransferDestination.INTERNATIONAL
        : BeneficiaryTransferDestination.LOCAL

      form?.setValue('amount', maximumRefundableAmount?.toString())
      form?.setValue('maximumRefundAmount', maximumRefundableAmount?.toString())

      form?.setValue('transferDestination', transferDestination)
    }
  }, [isInternational, maximumRefundableAmount, isRefundSheetOpen])

  useEffect(() => {
    form?.setValue('fee', refundFeeEstimate?.fee?.value?.toString())
  }, [refundFeeEstimate?.fee?.value, isRefundSheetOpen])

  useEffect(() => {
    if (!isRefundSheetOpen) {
      setRefundFeeEstimateError(null)
    }
  }, [isRefundSheetOpen])

  const feeEstimate = useMemo(() => refundFeeEstimate?.fee?.value ?? 0, [refundFeeEstimate])

  const refundCost = useMemo(() => {
    const fee = Number(formValues?.fee) || 0
    const amount = Number(formValues?.amount ?? maxAvailableToRefund) || 0
    return fee + amount
  }, [formValues?.fee, formValues?.amount, maxAvailableToRefund])

  const walletBalanceIsInsufficient = useMemo(() => {
    return (wallet?.balance ?? 0) < refundCost
  }, [wallet?.balance, refundCost])

  useEffect(() => {
    if (walletBalanceIsInsufficient) {
      setWalletBalanceIsInsufficient(walletBalanceIsInsufficient)
    }
  }, [walletBalanceIsInsufficient])

  const handleOnChange = (field: RefundFormPayloadKeys, value: string) => {
    form?.setValue(field, value)
  }

  const validateAmount = (amount: number | string) => {
    const error = getAmountError(amount)

    if (error) {
      form.setError('amount', { message: error })
    } else {
      form.clearErrors('amount')
    }
  }

  const getAmountError = (amount: number | string) => {
    handleRefundFeeEstimate()
    const refundAmount =
      (maxAvailableToRefund ?? 0) > 0 ? maxAvailableToRefund : transaction?.amount
    const noAmount = !amount || parseFloat(amount as string) === 0

    const isInsufficientBalance = Number(amount) > refundAmount
    setIsInsufficientBalance(isInsufficientBalance || noAmount)
    const amountErrorMaximumRefundFees =
      refundFeeEstimate &&
      wallet &&
      t('amountErrorMaximumRefundFees', {
        amount: roundNumberWithCommas(refundAmount),
        currency: transaction?.wallet?.currency?.code,
      })

    if (isInsufficientBalance) {
      return amountErrorMaximumRefundFees
    }
  }

  const amountOnBlurHandler = async (event: React.FocusEvent<HTMLInputElement, Element>) => {
    setIsInsufficientBalance(false)

    const isValidAmount = await form?.trigger('amount')

    if (isValidAmount) {
      validateAmount(stringToDecimal(event?.target?.value))
    }
  }

  const referenceOnBlurHandler = async () => {
    const isValidReference = await form?.trigger('reference')

    if (isValidReference) {
      validateAmount(stringToDecimal(formValues?.amount))
    }
  }

  const currency = wallet?.currency?.code

  const refundEstimatePaymentReferenceError =
    refundFeeEstimateError?.data?.details?.errors?.paymentReference?.[0]
  const refundEstimatePaymentReferenceErrorMessage = refundFeeEstimateError?.data?.message as string

  const feeEstimateBody = useMemo(() => {
    if (isCreateRefundFeeEstimateRequestRequestLoading) {
      return t('wallets.fiatSend.calculatingFee')
    }
    if (refundFeeEstimateError) {
      return `${roundNumberWithCommas(0)} ${currency}`
    }
    return `${roundNumberWithCommas(feeEstimate)} ${currency}`
  }, [
    isCreateRefundFeeEstimateRequestRequestLoading,
    refundFeeEstimateError,
    feeEstimate,
    currency,
    t,
  ])

  const maxRefundAmountBody = useMemo(() => {
    if (isCreateRefundFeeEstimateRequestRequestLoading) {
      return t('wallets.fiatSend.calculatinAmount')
    }

    return `${roundNumberWithCommas(maxAvailableToRefund)} ${currency}`
  }, [
    isCreateRefundFeeEstimateRequestRequestLoading,
    refundFeeEstimateError,
    maxAvailableToRefund,
    currency,
  ])

  const handleConvert = () => {
    handleClose()
    handleOpenConvertSheet()
  }

  const isInvalidAmountError = !!form?.formState?.errors?.amount?.message

  const onContinue = async () => {
    const validateFields: RefundFormPayloadKeys[] = ['reference', 'amount']
    const verifyFields = await form?.trigger(validateFields)
    if (verifyFields) {
      setStep(RefundSteps.CONFIRMATION)
    }
  }

  const actionButtons = useMemo<[ReactElement, ReactElement]>(() => {
    const isDisabled =
      isCreateRefundFeeEstimateRequestError ||
      isInsufficientBalance ||
      isInvalidAmountError ||
      !formValues?.reference

    return [
      <Button key="cancel" testId="refund-cancel" variant="secondary" onClick={() => handleClose()}>
        {t('cancel')}
      </Button>,
      <Button
        key="continue"
        testId="refund-continue"
        onClick={onContinue}
        loading={isCreateRefundFeeEstimateRequestRequestLoading}
        disabled={isDisabled}
      >
        {t('continue')}
      </Button>,
    ]
  }, [
    enableUsLaunchRestrictions,
    isCreateRefundFeeEstimateRequestError,
    isInsufficientBalance,
    isInvalidAmountError,
    isCreateRefundFeeEstimateRequestRequestLoading,
  ])

  return (
    <Box>
      <Box flexCol paddingT={16}>
        {walletBalanceIsInsufficient && !isPartiallyRefundable ? (
          <Box paddingY={8}>
            <Box
              flex
              flexCol
              justify="center"
              align="center"
              className="max-w-[500px] gap-5 text-center"
              data-testId="warning"
            >
              <Illustration name="Warning" />
              <h2 className="text-foreground-prominent text-h2">
                {t('refundWalletTopUpRequired')}
              </h2>
              <p className="text-foreground-prominent text-body-regular">{t('convertFunds')}</p>
              <Button onClick={handleConvert}>{t('convert')}</Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box paddingY={8}>
              <Card variant="static" emphasis full>
                <DataListItem
                  id="refund-fee-estimate"
                  title={t('transactionFee')}
                  body={feeEstimateBody}
                />
                <DataListItem
                  id="maximum-refund-amount"
                  title={t('maximumRefundAmount')}
                  body={maxRefundAmountBody}
                />
                {walletDescription && (
                  <DataListItem
                    id="wallet-description"
                    title={t('refundWallet')}
                    body={transaction?.wallet?.description}
                  />
                )}
              </Card>
            </Box>

            <Box paddingY={8}>
              <Input
                {...form?.register('amount')}
                label={t('amount')}
                testId="amount"
                rightSuffixText={currency}
                type="text"
                inputMode="numeric"
                required
                disabled={
                  !isPartiallyRefundable || !!isCreateRefundFeeEstimateRequestRequestLoading
                }
                onBlur={amountOnBlurHandler}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleOnChange('amount', e.target.value)
                }
                error={!!refundFeeEstimateError || !!form.formState?.errors?.amount?.message}
                errorText={
                  refundFeeEstimateError
                    ? refundEstimatePaymentReferenceErrorMessage || t('fetchingFeeError')
                    : form.formState?.errors?.amount?.message
                }
                loading={isCreateRefundFeeEstimateRequestRequestLoading}
              />
            </Box>
            <Box paddingT={8} paddingB={12}>
              <Input
                {...form?.register('reference')}
                testId="refund-reference"
                label={t('reference')}
                placeholder={t('wallets.fiatSend.referencePlaceholder')}
                errorText={
                  refundEstimatePaymentReferenceError ||
                  form.formState?.errors?.reference?.message ||
                  t('refundReferenceError')
                }
                error={
                  !!refundEstimatePaymentReferenceError ||
                  !!form.formState?.errors?.reference?.message ||
                  formValues?.reference?.length === 0
                }
                value={formValues?.reference || ''}
                onBlur={referenceOnBlurHandler}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleOnChange('reference', e.target.value)
                }
              />
            </Box>
            {actionButtons && (
              <Box flex justify="between" paddingT={20}>
                {actionButtons}
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
